import AppContext from 'App/AppContext';
import { IPLabel } from 'baseComponents';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { Role } from 'models/auth/role';
import { IOwner, Owner } from 'models/owner';
import User from 'models/user';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserRoleGroupPicker, {
  getUserRoleGroupItem,
  IUserRoleGroup,
  renderUserRoleGroupItem,
} from './UserRoleGroupPicker';

interface IOwnerPickerProps {
  onSelect?: (item: User | Role | undefined, owner: IOwner | undefined) => void;
  selectedItem?: IOwner;
  selectedItemId?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  disallowRoles?: boolean;
}

export const OwnerPicker = (props: IOwnerPickerProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation']);
  const [owner, setOwner] = useState<Owner | undefined>(undefined);

  useEffect(() => {
    if (props.selectedItem) {
      setOwner(new Owner(props.selectedItem, appContext));
    } else if (props.selectedItemId) {
      const owner = new Owner();
      owner.appContext = appContext;
      owner.setId(props.selectedItemId);
      setOwner(owner);
    } else {
      setOwner(new Owner(undefined, appContext));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedItem, props.selectedItemId]);

  //
  // Main render
  //
  if (!owner) {
    return null;
  }

  return (
    <IPStack>
      {!props.hideLabel && <IPLabel>{t('translation:General.OwnerPicker.Label')}</IPLabel>}
      <UserRoleGroupPicker
        allowedItems={[]}
        itemLimit={1}
        selectedItems={owner.itemAsArray}
        onSelect={(items) => {
          const newOwner = new Owner();
          const newItem = items[0] as User | Role | undefined;
          newOwner.fromItem(newItem);
          if (props.onSelect) props.onSelect(newItem, newOwner);
        }}
        disabled={props.disabled}
        placeholder={
          props.disallowRoles === true
            ? t('translation:General.OwnerPicker.PlaceholderUser')
            : t('translation:General.OwnerPicker.Placeholder')
        }
        allowGroups={false}
        allowRoles={props.disallowRoles === true ? false : true}
        allowUsers={true}
        onRemove={() => {
          if (props.onSelect) props.onSelect(undefined, undefined);
        }}
      />
    </IPStack>
  );
};

interface IOwnerItemProps {
  owner?: IOwner;
  allowHoverCard?: boolean;
  showNoOwner?: boolean;
  textColor?: string;
}

export const OwnerItem = (props: IOwnerItemProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation']);

  const getItem = (): IUserRoleGroup | undefined => {
    const owner = new Owner(props.owner, appContext);
    const item = owner.item;

    return item ? getUserRoleGroupItem(item) : undefined;
  };

  //
  // Main render
  //
  const item = getItem();

  if (item && item.id) {
    return renderUserRoleGroupItem(
      getItem(),
      true,
      undefined,
      props.allowHoverCard ?? false,
      0,
      undefined,
      true,
      undefined,
      props.textColor,
    );
  } else if (props.showNoOwner === true) {
    return (
      <IPPersona
        size={IPPersonaSize.size24}
        text={t('translation:General.OwnerPicker.NoOwner')}
        coinProps={{ showUnknownPersonaCoin: true }}
      />
    );
  }

  return null;
};
